import React, { useState, useEffect } from "react";
import useFeedbackService from "../../../../services/feedbackService";
import ScrollToHash from "../../../../utils/ScrollToHash";
import { Loader2 } from "lucide-react";
import {
  FileTextOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { Row, Col, Card, Button, DatePicker, Select, message } from "antd";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import useReportService from "../../../../services/reportService";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import LastReportTable from "./LastReportTable";
import HeaderCards from "./HeaderCards";
import Trends from "./Trends";
import FormRanking from "./FormRanking";
import KISuggestions from "./KISuggestions";
import { useAuth } from "../../../../context/authContext";
import SubscriptionOverlay from "../../../UI/SubscriptionOverlay";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const { RangePicker } = DatePicker;
const { Option } = Select;

const ReportsAnalysis = () => {
  const [reportPeriod, setReportPeriod] = useState(null);
  const [trendRange, setTrendRange] = useState("7d");
  const [activeKey, setActiveKey] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [forms, setForms] = useState([]);
  const [chartMetrics, setChartMetrics] = useState(null);
  const { subscriptionLevel, user, fetchUser } = useAuth();
  const { getFeedbackForms } = useFeedbackFormService();
  const [reports, setReports] = useState([]);
  const [reportsFlag, setReportsFlag] = useState(false);
  const { getChartMetrics } = useFeedbackService();
  const [loading, setLoading] = useState(false);
  const {
    getReportsForUser,
    generateAggregatedKIReport,
    generateAggregatedReport,
    generateFormKIReport,
    generateFormReport,
  } = useReportService();
  const { language } = useLanguage();
  const text = dashboardContent[language].reports.analysis;

  const isLimitReached = (used, limit) =>
    typeof used === "number" && typeof limit === "number" && used >= limit;

  const needsSubscriptionUpgrade = (key) =>
    subscriptionLevel < 2 && (key === "3" || key === "4");

  useEffect(() => {
    const getForms = async () => {
      const data = await getFeedbackForms();
      setForms(data);
    };
    getForms();
  }, [reportPeriod]);

  useEffect(() => {
    const getReports = async () => {
      const res = await getReportsForUser();
      const sortedReports = res.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setReports(sortedReports);
    };
    getReports();
  }, [reportsFlag]);

  useEffect(() => {
    if (!trendRange) return;

    const fetchChartMetrics = async () => {
      try {
        const metrics = await getChartMetrics(trendRange);
        setChartMetrics(metrics);
      } catch (error) {
        console.error("Fehler beim Laden der Diagramm-Metriken:", error);
        message.error("Diagrammdaten konnten nicht geladen werden.");
      }
    };

    fetchChartMetrics();
  }, [trendRange]);

  const { reportLimits, reportUsage } = user;

  const reportCards = [
    {
      key: "1",
      title: text.titleFeedbackReport, // Dynamischer Titel
      description: text.descriptionFeedbackReport, // Dynamische Beschreibung
      usage: reportUsage.totalReports,
      limit: reportLimits.totalReports,
      icon: <FileTextOutlined className="text-3xl text-blue-500" />,
      actionLabel: text.viewReport, // Dynamische Action Label
      action: () => console.log("Gesamtbericht geöffnet"),
    },
    {
      key: "2",
      title: text.titleIndividualReport, // Dynamischer Titel
      description: text.descriptionIndividualReport, // Dynamische Beschreibung
      usage: reportUsage.formReports,
      limit: reportLimits.formReports,
      icon: <BarChartOutlined className="text-3xl text-green-500" />,
      actionLabel: text.selectForm, // Dynamische Action Label
      action: () => console.log("Einzelbericht geöffnet"),
    },
    {
      key: "3",
      title: text.titleAIReport, // Dynamischer Titel
      description: text.descriptionAIReport, // Dynamische Beschreibung
      usage: reportUsage.kiTotalReports,
      limit: reportLimits.kiTotalReports,
      icon: <LineChartOutlined className="text-3xl text-orange-500" />,
      actionLabel: text.startAIAnalysis, // Dynamische Action Label
      action: () => console.log("KI-Analyse gestartet"),
    },
    {
      key: "4",
      title: text.titleAISingleReport, // Dynamischer Titel
      description: text.descriptionAISingleReport, // Dynamische Beschreibung
      usage: reportUsage.kiFormReports,
      limit: reportLimits.kiFormReports,
      icon: <PieChartOutlined className="text-3xl text-purple-500" />,
      actionLabel: text.selectFormAI, // Dynamische Action Label
      action: () => console.log("KI-Einzelanalyse gestartet"),
    },
  ];

  // Je nach activeKey und ReportPeriod (und ggf. ausgewähltem Formular) wird der entsprechende Service-Aufruf getätigt
  const handleExportPDF = async () => {
    setLoading(true);
    if (!activeKey || !reportPeriod) {
      message.error(text.periodError);
      return;
    }
    const [start, end] = reportPeriod;

    try {
      switch (activeKey) {
        case "1":
          await generateAggregatedReport({ start, end, language });
          await fetchUser(); // Limits aktualisieren
          setReportsFlag(!reportsFlag);
          break;
        case "2":
          if (!selectedFormId) {
            message.error(text.formError);
            return;
          }
          await generateFormReport({
            formId: selectedFormId,
            start,
            end,
            language,
          });
          await fetchUser();
          setReportsFlag(!reportsFlag);
          break;
        case "3":
          await generateAggregatedKIReport({ start, end, language });
          await fetchUser();
          setReportsFlag(!reportsFlag);
          break;
        case "4":
          if (!selectedFormId) {
            message.error(text.formError);
            return;
          }
          await generateFormKIReport({
            formId: selectedFormId,
            start,
            end,
            language,
          });
          await fetchUser();
          setReportsFlag(!reportsFlag);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Fehler beim Exportieren des Berichts:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const isExportDisabled =
    loading ||
    !reportPeriod ||
    !activeKey ||
    subscriptionLevel < 2 ||
    ((activeKey === "2" || activeKey === "4") && !selectedFormId);

  return (
    <div className="p-6 bg-gray-100 min-h-screen" id="stats">
      <ScrollToHash />
      {/* Titel für Analysen */}
      <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 mt-4 text-gray-800">
        {text.titleFeedbackAnalysis}
      </h1>
      {/* Karten (Statistiken) */}
      <HeaderCards />
      <Row gutter={[16, 16]}>
        {/* KI-Verbesserungsvorschläge */}
        <Col xs={24} md={10}>
          <KISuggestions />
        </Col>

        {/* Ranking */}
        <Col xs={24} md={14}>
          <FormRanking chartMetrics={chartMetrics} />
        </Col>
      </Row>

      {/* Trends*/}
      <>
        <div className="flex justify-end my-4">
          <Select
            value={trendRange}
            onChange={(value) => setTrendRange(value)}
            className="w-full md:w-48"
          >
            <Option value="7d">{text.dateRanges.last7Days}</Option>
            <Option value="30d">{text.dateRanges.last30Days}</Option>
            <Option value="year">{text.dateRanges.thisYear}</Option>
          </Select>
        </div>
      </>
      <Trends chartMetrics={chartMetrics} />
      {/* Reporting-Bereich */}
      <h1
        className="text-2xl md:text-3xl font-bold text-center mb-6 mt-10 text-gray-800"
        id="reports"
      >
        {text.titleReportGeneration}
      </h1>
      {/* Auswahl des Berichts */}
      <Row gutter={[16, 16]} className="mb-4">
        {reportCards.map((card) => {
          const isActive = card.key === activeKey;
          const limitReached = isLimitReached(card.usage, card.limit);
          const showOverlay = needsSubscriptionUpgrade(card.key);

          return (
            <Col xs={24} sm={12} md={12} lg={12} key={card.key}>
              <Card
                className={`p-4 rounded-lg h-[290px] transition-all ${
                  isActive
                    ? "border-blue-500 shadow-xl scale-105 bg-blue-50"
                    : "bg-white shadow-md hover:shadow-lg"
                } ${
                  limitReached
                    ? "opacity-50 pointer-events-none"
                    : "cursor-pointer"
                }`}
                bordered={isActive}
                onClick={() => {
                  if (!limitReached && !showOverlay) {
                    setActiveKey(card.key);
                    if (card.key === "2" || card.key === "4") {
                      setSelectedFormId("");
                    }
                  }
                }}
              >
                {showOverlay ? (
                  <SubscriptionOverlay
                    onUpgrade={() => (window.location.href = "/pricing")}
                    blur={false}
                  />
                ) : (
                  <div className="p-4">
                    <div className="flex flex-col items-center mb-3">
                      {card.icon}
                      <span
                        className={`text-xs font-medium mt-1 ${
                          limitReached ? "text-red-500" : "text-gray-600"
                        }`}
                      >
                        {card.usage}/{card.limit}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold">{card.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {card.description}
                    </p>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Dropdown zur Formularauswahl, wenn aktiv "Individueller Bericht" oder "KI-Analyse – Einzelne Formulare" */}
      {(activeKey === "2" || activeKey === "4") && (
        <Row className="mb-4">
          <Col xs={24} sm={12} md={6}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {text.formSelectLabel}
            </label>

            <Select
              placeholder={text.formSelectPlaceholder}
              style={{ width: "100%" }}
              value={selectedFormId || undefined}
              onChange={(value) => setSelectedFormId(value)}
            >
              {forms.map((form) => (
                <Option key={form._id} value={form._id}>
                  {form.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      )}
      {/* Auswahl des Berichtszeitraums */}
      <Row
        className="mt-4"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        <Col xs={24} md={12}>
          <RangePicker
            style={{ width: "100%" }}
            onChange={(dates) => {
              if (dates) {
                setReportPeriod(dates.map((date) => date.toISOString()));
              } else {
                setReportPeriod(null);
              }
            }}
          />
        </Col>
        <Col xs={24} md={12} className="flex justify-end">
          <Button
            type="primary"
            className="bg-orange-500 w-full md:w-auto"
            disabled={isExportDisabled}
            onClick={handleExportPDF}
          >
            {text.exportButtonLabel}
          </Button>
        </Col>
      </Row>
      <LastReportTable reports={reports} />
      {loading && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 bg-white/90 shadow-lg rounded-full p-3 flex items-center space-x-3 border border-gray-200">
          <Loader2 className="animate-spin h-5 w-5 text-orange-500" />
          <span className="text-sm font-medium text-gray-700">
            Creating Report...
          </span>
        </div>
      )}
    </div>
  );
};

export default ReportsAnalysis;
